import type { Column, Row, RowData } from '@tanstack/react-table';
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    fromIso,
    now,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears,
    toIso,
} from '@this/shared/dist/tools/datetime.js';
import { DATE_FILTER_OPERATION } from './operation.js';
import { type DateFilterSchema, dateFilterSchema, dateValueSchema } from './schema.js';

export function typedSetDateColumnFilterValue<TData extends RowData>(
    column: Column<TData, unknown>,
    filterValue: DateFilterSchema
) {
    column.setFilterValue(filterValue);
}

export function dateFilter<TData extends RowData>(row: Row<TData>, columnId: string, filterValue: DateFilterSchema) {
    const filter = dateFilterSchema.parse(filterValue);
    const value = dateValueSchema.parse(row.getValue(columnId));

    if (filter.operation === DATE_FILTER_OPERATION.inArray.id) {
        const isBlank = value === null || value === undefined;

        if (filter.includeBlanks && isBlank) {
            return true;
        }

        if (isBlank) {
            return false;
        }

        return filter.array.some((arrayValue) => {
            return arrayValue && toIso(startOfDay(value)) === toIso(startOfDay(fromIso(arrayValue)));
        });
    }

    if (value === null || value === undefined) {
        // TODO: Decide how we handle empty values

        return false;
    } else if (
        ((filter.operation === DATE_FILTER_OPERATION.equals.id ||
            filter.operation === DATE_FILTER_OPERATION.before.id ||
            filter.operation === DATE_FILTER_OPERATION.after.id) &&
            filter.value1 === undefined) ||
        (filter.operation === DATE_FILTER_OPERATION.between.id &&
            filter.value1 === undefined &&
            filter.value2 === undefined)
    ) {
        return true;
    }

    const today = startOfDay(now());
    const selectedDay = startOfDay(value);

    switch (filter.operation) {
        case DATE_FILTER_OPERATION.between.id: {
            const { value1: from, value2: to } = filter;

            if (from && to) {
                return toIso(value) >= toIso(startOfDay(from)) && toIso(value) <= toIso(endOfDay(to));
            } else if (from) {
                return toIso(value) >= toIso(startOfDay(from)) && toIso(value) <= toIso(endOfDay(from));
            } else if (to) {
                return toIso(value) >= toIso(startOfDay(to)) && toIso(value) <= toIso(endOfDay(to));
            } else {
                return true;
            }
        }
        case DATE_FILTER_OPERATION.equals.id: {
            return filter.value1 !== undefined && toIso(selectedDay) === toIso(startOfDay(filter.value1));
        }
        case DATE_FILTER_OPERATION.before.id: {
            return filter.value1 !== undefined && toIso(selectedDay) < toIso(startOfDay(filter.value1));
        }
        case DATE_FILTER_OPERATION.after.id: {
            return filter.value1 !== undefined && toIso(selectedDay) > toIso(startOfDay(filter.value1));
        }
        case DATE_FILTER_OPERATION.tomorrow.id: {
            return toIso(selectedDay) === toIso(startOfDay(addDays(now(), 1)));
        }
        case DATE_FILTER_OPERATION.today.id: {
            return toIso(selectedDay) === toIso(today);
        }
        case DATE_FILTER_OPERATION.yesterday.id: {
            return toIso(selectedDay) === toIso(startOfDay(subDays(now(), 1)));
        }
        case DATE_FILTER_OPERATION.nextWeek.id: {
            const nextMonday = startOfWeek(addWeeks(today, 1));
            const nextSunday = endOfWeek(nextMonday);

            return toIso(value) >= toIso(nextMonday) && toIso(value) <= toIso(nextSunday);
        }
        case DATE_FILTER_OPERATION.thisWeek.id: {
            const thisMonday = startOfWeek(today);
            const thisSunday = endOfWeek(today);

            return toIso(value) >= toIso(thisMonday) && toIso(value) <= toIso(thisSunday);
        }
        case DATE_FILTER_OPERATION.lastWeek.id: {
            const lastMonday = startOfWeek(subWeeks(today, 1));
            const lastSunday = endOfWeek(lastMonday);

            return toIso(value) >= toIso(lastMonday) && toIso(value) <= toIso(lastSunday);
        }
        case DATE_FILTER_OPERATION.nextMonth.id: {
            const nextMonthStart = startOfMonth(addMonths(today, 1));
            const nextMonthEnd = endOfMonth(nextMonthStart);

            return toIso(value) >= toIso(nextMonthStart) && toIso(value) <= toIso(nextMonthEnd);
        }
        case DATE_FILTER_OPERATION.thisMonth.id: {
            const thisMonthStart = startOfMonth(today);
            const thisMonthEnd = endOfMonth(today);

            return toIso(value) >= toIso(thisMonthStart) && toIso(value) <= toIso(thisMonthEnd);
        }
        case DATE_FILTER_OPERATION.lastMonth.id: {
            const lastMonthStart = startOfMonth(subMonths(today, 1));
            const lastMonthEnd = endOfMonth(lastMonthStart);

            return toIso(value) >= toIso(lastMonthStart) && toIso(value) <= toIso(lastMonthEnd);
        }
        case DATE_FILTER_OPERATION.nextQuarter.id: {
            const nextQuarterStart = startOfQuarter(addMonths(today, 3));
            const nextQuarterEnd = endOfQuarter(nextQuarterStart);

            return toIso(value) >= toIso(nextQuarterStart) && toIso(value) <= toIso(nextQuarterEnd);
        }
        case DATE_FILTER_OPERATION.thisQuarter.id: {
            const thisQuarterStart = startOfQuarter(today);
            const thisQuarterEnd = endOfQuarter(today);

            return toIso(value) >= toIso(thisQuarterStart) && toIso(value) <= toIso(thisQuarterEnd);
        }
        case DATE_FILTER_OPERATION.lastQuarter.id: {
            const lastQuarterStart = startOfQuarter(subMonths(today, 3));
            const lastQuarterEnd = endOfQuarter(lastQuarterStart);

            return toIso(value) >= toIso(lastQuarterStart) && toIso(value) <= toIso(lastQuarterEnd);
        }
        case DATE_FILTER_OPERATION.nextYear.id: {
            const nextYearStart = startOfYear(addYears(today, 1));
            const nextYearEnd = endOfYear(nextYearStart);

            return toIso(value) >= toIso(nextYearStart) && toIso(value) <= toIso(nextYearEnd);
        }
        case DATE_FILTER_OPERATION.thisYear.id: {
            const thisYearStart = startOfYear(today);
            const thisYearEnd = endOfYear(today);

            return toIso(value) >= toIso(thisYearStart) && toIso(value) <= toIso(thisYearEnd);
        }
        case DATE_FILTER_OPERATION.lastYear.id: {
            const lastYearStart = startOfYear(subYears(today, 1));
            const lastYearEnd = endOfYear(lastYearStart);

            return toIso(value) >= toIso(lastYearStart) && toIso(value) <= toIso(lastYearEnd);
        }
        case DATE_FILTER_OPERATION.yearToDate.id: {
            const yearStart = startOfYear(today);

            return toIso(value) >= toIso(yearStart) && toIso(value) <= toIso(endOfDay(today));
        }
        default: {
            return false;
        }
    }
}
